import GasMeterIcon from "@mui/icons-material/GasMeter";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useNotifications } from "@toolpad/core";
import { useState } from "react";
import * as XLSX from "xlsx";
import { submitStavVodomeru } from "../services/odbernaMistaService";
import { formatDate } from "../utils/dateUtils";
import CustomTable from "./CustomTable";
import ModalWindow from "./modal/ModalWindow";
import VodomerReadingForm from "./VodomerReadingForm";

const getPeriodaOdectuLabel = (periodaOdectu) => {
  switch (periodaOdectu) {
    case "M":
      return "Měsíční";
    case "Q":
      return "Čtvrtletní";
    case "P":
      return "Pololetní";
    case "R":
      return "Roční";
    default:
      return "-";
  }
};

export default function Vodomery({
  vodomery,
  setAllMeters,
  today,
  zobrazitVyberTypu,
  showOdberatelNazev = false,
  onRefreshData,
}) {
  const [modalVisible, setModalVisible] = useState(false);
  const [activeVodomer, setActiveVodomer] = useState(null);
  const notifications = useNotifications();

  const handleSubmitStav = async (payload) => {
    setModalVisible(false);

    const result = await submitStavVodomeru(payload);

    if (result.success) {
      if (onRefreshData) {
        await onRefreshData();
      } else {
        setAllMeters((prev) =>
          prev.map((vodomer) =>
            vodomer.cisloVodomeru === activeVodomer.cisloVodomeru
              ? { ...vodomer, lastStav: payload.stav, jizZadano: true }
              : vodomer
          )
        );
      }
      notifications.show("Stav byl úspěšně odeslán!", {
        severity: "success",
        autoHideDuration: 3000,
      });
    } else {
      notifications.show(result.message, {
        severity: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const handleZadatStavBtnClick = (vdm) => {
    setActiveVodomer(vdm);
    setModalVisible(true);
  };

  const getExportRows = () => {
    return vodomery.map((item) => {
      const row = {
        "Číslo OM": item.cisloOm ?? "",
        Adresa: [item.ulice, item.obec].filter(Boolean).join(", "),
        ...(showOdberatelColumn
          ? { Odběratel: item.odberatelNazev ?? "" }
          : {}),
        "Číslo VM": item.cisloVodomeru ?? "",
        "Číslo plomby": item.cisloPlomby ?? "",
        "Datum montáže": item.datMontaze ? formatDate(item.datMontaze) : "",
        "Poslední odečet": item.lastOdecet ? formatDate(item.lastOdecet) : "",
        "Poslední stav": item.lastStav != null ? item.lastStav : "",
        "Již zadáno": item.jizZadano ? "Ano" : "Ne",
      };

      return row;
    });
  };

  const getExportFilePrefix = () => {
    const now = new Date();
    const dateStamp = now.toISOString().slice(0, 10);
    return `stavy_vodomeru_${dateStamp}`;
  };

  const handleExportXlsx = () => {
    const rows = getExportRows();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stavy vodoměrů");
    XLSX.writeFile(workbook, `${getExportFilePrefix()}.xlsx`, {
      compression: true,
    });
  };

  // true pokud alespoň jeden vodoměr má číslo OM
  const showOmColumn = vodomery?.some((vdm) => !!vdm.cisloOm);
  const showOdberatelColumn =
    showOdberatelNazev && vodomery?.some((vdm) => vdm.odberatelNazev != null);

  // sloupce pro CustomTable
  const columns = [
    ...(showOmColumn
      ? [
          { id: "cisloOm", label: "Číslo OM" },
              ...(showOdberatelColumn
                ? [
                    {
                      id: "odberatelNazev",
                      label: "Odběratel",
                      renderCell: (value) => value ?? "",
                    },
                  ]
            : []),
          { id: "adresa", label: "Adresa" },
          {
            id: "periodaOdectu",
            label: "Typ zadání",
            renderCell: (value) => getPeriodaOdectuLabel(value),
          },
        ]
      : []),
    { id: "cisloVodomeru", label: "Číslo VM" },
    { id: "cisloPlomby", label: "Číslo plomby" },
    {
      id: "datMontaze",
      label: "Datum montáže",
      renderCell: (value) => (value ? formatDate(value) : "-"),
    },

    {
      id: "lastOdecet",
      label: "Poslední odečet",
      renderCell: (value) => (value ? formatDate(value) : "-"),
    },
    {
      id: "lastStav",
      label: "Poslední stav",
      renderCell: (value) => (value != null ? value : "-"),
    },
    {
      id: "action",
      label: "Akce",
      renderCell: (_, row) => {
        const disabled = !row.muzeDnesZadat;
        return disabled ? (
          <Button startIcon={<GasMeterIcon />} variant="contained" disabled>
            Zadat stav
          </Button>
        ) : (
          <Button
            startIcon={<GasMeterIcon />}
            variant="contained"
            onClick={() => handleZadatStavBtnClick(row)}
          >
            Zadat stav
          </Button>
        );
      },
    },
  ];

  if (!vodomery) {
    return (
      <Box sx={{ textAlign: "center", p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {showOmColumn && vodomery.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            startIcon={<FileDownloadIcon />}
            variant="contained"
            onClick={handleExportXlsx}
          >
            Export XLSX
          </Button>
        </Box>
      )}

      {vodomery.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
          Nebyly nalezeny žádné záznamy.
        </Typography>
      ) : (
        <CustomTable
          columns={columns}
          data={vodomery}
          getRowKey={(row) => row.cisloVodomeru}
        />
      )}

      {/* Dialog zadaní stavu vodoměru */}
      <ModalWindow visible={modalVisible} setVisible={setModalVisible}>
        <VodomerReadingForm
          key={activeVodomer?.cisloVodomeru}
          activeVodomer={activeVodomer}
          today={activeVodomer?.dnes ?? today}
          zobrazitVyberTypu={activeVodomer?.zobrazitVyberTypu ?? zobrazitVyberTypu}
          onSubmit={handleSubmitStav}
        />
      </ModalWindow>
    </Box>
  );
}
